import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  openAssignmentSchema,
  roleSchema,
  unitsResponseSchema,
  setPasswordResponseSchema,
  userDetailResponseSchema,
  userStatusSchema,
  type OpenAssignment,
  type Role,
  type UserDetail,
  type UserStatus,
  personName,
} from '@unit/shared';
import { z } from 'zod';
import { useApi } from '../../lib/useApi.js';
import { ApiClientError, apiPatch, apiPost, apiPut } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ActionError } from '../../components/ActionError.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';
import { ApiError } from '../../components/ApiError.js';
import { Field } from '../../components/Field.js';
import { GearCard } from '../gear/GearCard.js';
import { PersonalDetailsForm } from './PersonalDetailsForm.js';
import { COMMANDER_ROLES, PLATOON_LEADER_ROLES, PERSONNEL_ROLES } from '../../components/RequireRole.js';
import { TitlePicker } from './TitlePicker.js';
import { unitOptions } from '../units/unitOptions.js';

/**
 * One soldier's file (REQUIREMENTS §2.6, "soldier page").
 *
 * Every control below is shown only to a caller the server would let use it
 * (owner, 2026-09-19: "if you don't have permission, you should not see it").
 * That is a convenience, not the control: the endpoints refuse regardless,
 * and that refusal is what actually protects them. The gates here mirror
 * `authz.ts` — and they can be role-only because this page is reached only
 * for a soldier within the caller's reach, so scope is already settled.
 */
export function SoldierDetailPage() {
  const { t } = useTranslation();
  const { unitId, uid } = useParams<{ unitId: string; uid: string }>();
  const { roles: callerRoles } = useClaimRoles();
  const isAdmin = callerRoles.includes('admin');
  // Name, title and personal details: the people-wide roles, or a platoon
  // leader for their own chain — `canEditUser` server-side; a team commander
  // views and never edits. Moving between units is offered to the admin and
  // the platoon leaders (story 17 kept it off the personnel roles' screen).
  const canEditDetails = callerRoles.some((r) => PERSONNEL_ROLES.includes(r) || PLATOON_LEADER_ROLES.includes(r));
  const canMove = isAdmin || callerRoles.some((r) => PLATOON_LEADER_ROLES.includes(r));
  // Issuing: the commander roles — `canIssueTo`. The CHRS reads the file and
  // never touches gear, so the button is not offered to them.
  const canIssue = callerRoles.some((r) => COMMANDER_ROLES.includes(r));

  const state = useApi(uid ? `/users/${uid}` : null, userDetailResponseSchema);

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;

  const user = state.data.user;

  return (
    <>
      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900" data-testid="soldier-name">
            {personName(user)}
          </h2>
          <span className="flex items-center gap-3">
            {canIssue && user.status === 'active' && (
              <Link
                to={`/issue?soldier=${user.uid}`}
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700"
                data-testid="issue-gear"
              >
                {t('issue.button')}
              </Link>
            )}
            {canIssue && (
              // The soldier's own traffic in the ledger (story 41): the same
              // reach as issuing, checked again by the server.
              <Link to={`/ledger?soldierUid=${user.uid}`} className="text-sm text-slate-600 underline-offset-4 hover:underline" data-testid="soldier-ledger">
                {t('users.ledger')}
              </Link>
            )}
            <Link
              to={unitId ? `/org/${unitId}` : '/org'}
              className="text-sm text-slate-500 underline-offset-4 hover:underline"
            >
              {t('users.backToRoster')}
            </Link>
          </span>
        </div>

        <dl className="mt-4 divide-y divide-slate-100">
          <Field label={t('org.columns.personalNumber')} latin>
            {user.personalNumber}
          </Field>
          <Field label={t('users.rank')}>
            <span data-testid="soldier-rank">{user.rank ?? '—'}</span>
          </Field>
          <Field label={t('org.columns.title')}>
            <span data-testid="soldier-title">{user.title ?? '—'}</span>
          </Field>
          <Field label={t('users.status')}>
            <span data-testid="soldier-status">{t(`status.${user.status}`)}</span>
          </Field>
          <Field label={t('org.columns.roles')}>
            <span data-testid="soldier-roles">
              {user.roles.map((role) => t(`roles.${role}`)).join(' · ')}
            </span>
          </Field>
          <Field label={t('users.email')} latin>
            {user.email ?? '—'}
          </Field>
          <Field label={t('users.phone')} latin>
            {user.phone ?? '—'}
          </Field>
        </dl>
      </section>

      <GearCard uid={user.uid} title={t('gear.title')} />

      {canEditDetails && <IdentityCard user={user} onChanged={state.reload} />}

      <PersonalDetailsCard user={user} editable={canEditDetails} onChanged={state.reload} />

      {canMove && <MoveTeamCard user={user} onChanged={state.reload} />}

      {isAdmin && (
        <>
          <RolesCard user={user} onChanged={state.reload} />
          <SetPasswordCard user={user} />
          <StatusCard user={user} onChanged={state.reload} />
        </>
      )}
    </>
  );
}

const inputClass =
  'mt-1 block w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-slate-900';

/**
 * Name, rank and title (owner, 2026-09-19, story 38): the rank sits beside
 * the name rather than inside it, and the title is a pick from the
 * company's list. Same PATCH as the personal details, same editors.
 */
function IdentityCard({ user, onChanged }: { user: UserDetail; onChanged: () => void }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({ firstName: user.firstName, lastName: user.lastName, rank: user.rank ?? '', title: user.title ?? '' });
  const save = useAction(() =>
    apiPatch(`/users/${user.uid}`, userDetailResponseSchema, {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      rank: form.rank.trim() || null,
      title: form.title.trim() || null,
    }),
  );
  const set = (key: 'firstName' | 'lastName' | 'rank') => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="identity-card">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-semibold text-slate-900">{t('users.identity')}</h3>
        {!editing && (
          <button
            type="button"
            onClick={() => {
              setForm({ firstName: user.firstName, lastName: user.lastName, rank: user.rank ?? '', title: user.title ?? '' });
              setEditing(true);
              setSaved(false);
            }}
            data-testid="edit-identity"
            className="text-sm text-slate-600 underline-offset-4 hover:underline"
          >
            {t('users.editIdentity')}
          </button>
        )}
      </div>
      {editing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save.run().then((r) => {
              if (r) {
                setEditing(false);
                setSaved(true);
                onChanged();
              }
            });
          }}
          className="mt-3 grid gap-3 sm:grid-cols-2"
          data-testid="identity-form"
        >
          <label className="block text-sm text-slate-700">
            {t('users.firstName')}
            <input className={inputClass} value={form.firstName} onChange={set('firstName')} required data-testid="identity-first-name" />
          </label>
          <label className="block text-sm text-slate-700">
            {t('users.lastName')}
            <input className={inputClass} value={form.lastName} onChange={set('lastName')} required data-testid="identity-last-name" />
          </label>
          <label className="block text-sm text-slate-700">
            {t('users.rank')}
            <input className={inputClass} value={form.rank} onChange={set('rank')} data-testid="identity-rank" />
          </label>
          <label className="block text-sm text-slate-700">
            {t('org.columns.title')}
            <TitlePicker className={inputClass} value={form.title} onChange={(title) => setForm((f) => ({ ...f, title }))} testId="identity-title" />
          </label>
          <div className="flex gap-2 sm:col-span-2">
            <button
              type="submit"
              disabled={save.busy}
              data-testid="identity-submit"
              className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40"
            >
              {save.busy ? t('users.saving') : t('users.save')}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={save.busy}
              className="rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100"
            >
              {t('users.cancel')}
            </button>
          </div>
          <div className="sm:col-span-2">
            <ActionError error={save.error} />
          </div>
        </form>
      ) : (
        saved && (
          <p className="mt-2 text-sm text-emerald-700" data-testid="identity-saved">
            {t('users.identitySaved')}
          </p>
        )
      )}
    </section>
  );
}

/** Read-only rows of the personal details; shared with /me. */
export function PersonalDetailsRows({
  user,
  withPhone = false,
}: {
  user: Pick<UserDetail, 'phone' | 'personalDetails'>;
  /** The soldier page shows the phone in its header already; /me has no header. */
  withPhone?: boolean;
}) {
  const { t } = useTranslation();
  const d = user.personalDetails;
  return (
    <dl className="mt-3 divide-y divide-slate-100">
      {withPhone && (
        <Field label={t('users.phone')} latin>
          {user.phone ?? '—'}
        </Field>
      )}
      {/* A date is Latin script inside a Hebrew run and reverses without isolation. */}
      <Field label={t('users.dateOfBirth')} latin>
        {d.dateOfBirth ?? '—'}
      </Field>
      <Field label={t('users.bloodType')} latin>
        {d.bloodType ?? '—'}
      </Field>
      <Field label={t('users.address')}>{d.address ?? '—'}</Field>
      <Field label={t('users.sizes.shirt')} latin>
        {d.sizes.shirt ?? '—'}
      </Field>
      <Field label={t('users.sizes.pants')} latin>
        {d.sizes.pants ?? '—'}
      </Field>
      <Field label={t('users.sizes.boots')} latin>
        {d.sizes.boots ?? '—'}
      </Field>
      <Field label={t('users.sizes.vest')} latin>
        {d.sizes.vest ?? '—'}
      </Field>
      <Field label={t('users.emergencyContact')}>
        {d.emergencyContact.name ?? '—'}
        {d.emergencyContact.relation && ` (${d.emergencyContact.relation})`}
      </Field>
      <Field label={t('users.emergencyPhone')} latin>
        {d.emergencyContact.phone ?? '—'}
      </Field>
    </dl>
  );
}

function PersonalDetailsCard({
  user,
  editable,
  onChanged,
}: {
  user: UserDetail;
  editable: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const save = useAction((patch: object) => apiPatch(`/users/${user.uid}`, userDetailResponseSchema, patch));

  return (
    <section
      className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200"
      data-testid="personal-details"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-semibold text-slate-900">{t('users.personalDetails')}</h3>
        {editable && !editing && (
          <button
            type="button"
            onClick={() => {
              setEditing(true);
              setSaved(false);
            }}
            data-testid="edit-details"
            className="text-sm text-slate-600 underline-offset-4 hover:underline"
          >
            {t('users.editDetails')}
          </button>
        )}
      </div>
      {editing ? (
        <>
          <PersonalDetailsForm
            initial={{ phone: user.phone, personalDetails: user.personalDetails }}
            submitLabel={save.busy ? t('users.saving') : t('users.save')}
            busy={save.busy}
            onCancel={() => setEditing(false)}
            onSubmit={(patch) =>
              void save.run(patch).then((r) => {
                if (r) {
                  setEditing(false);
                  setSaved(true);
                  onChanged();
                }
              })
            }
          />
          <div className="mt-2">
            <ActionError error={save.error} />
          </div>
        </>
      ) : (
        <>
          {saved && (
            <p className="mt-2 text-sm text-emerald-700" data-testid="details-saved">
              {t('users.detailsSaved')}
            </p>
          )}
          <PersonalDetailsRows user={user} />
        </>
      )}
    </section>
  );
}

const ALL_ROLES = roleSchema.options;

function RolesCard({ user, onChanged }: { user: UserDetail; onChanged: () => void }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Role[]>(user.roles);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiClientError | null>(null);

  const toggle = (role: Role) =>
    setSelected((current) =>
      current.includes(role) ? current.filter((r) => r !== role) : [...current, role],
    );

  const save = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await apiPut(`/users/${user.uid}/roles`, userDetailResponseSchema, { roles: selected });
      onChanged();
    } catch (e) {
      setError(e instanceof ApiClientError ? e : null);
    } finally {
      setBusy(false);
    }
  }, [selected, user.uid, onChanged]);

  return (
    <section
      className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200"
      data-testid="roles-editor"
    >
      <h3 className="font-semibold text-slate-900">{t('users.assignRoles')}</h3>
      <p className="mt-1 text-sm text-slate-500">{t('users.assignRolesHint')}</p>

      <div className="mt-3 flex flex-wrap gap-2">
        {ALL_ROLES.map((role) => (
          <label
            key={role}
            className={`cursor-pointer rounded-lg px-3 py-1.5 text-sm ring-1 transition ${
              selected.includes(role)
                ? 'bg-slate-900 text-white ring-slate-900'
                : 'text-slate-600 ring-slate-300 hover:bg-slate-100'
            }`}
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={selected.includes(role)}
              onChange={() => toggle(role)}
            />
            {t(`roles.${role}`)}
          </label>
        ))}
      </div>

      {error && (
        <div className="mt-3">
          <ApiError code={error.code} requestId={error.requestId} />
        </div>
      )}

      <button
        type="button"
        onClick={() => void save()}
        disabled={busy || selected.length === 0}
        className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40"
      >
        {busy ? t('users.saving') : t('users.saveRoles')}
      </button>
    </section>
  );
}

/**
 * An admin sets the soldier's password (owner, 2026-09-13) — for a soldier
 * with no working mailbox. Typed once, sent once, never shown again; the
 * server stores it in Auth only and records that it happened.
 */
function SetPasswordCard({ user }: { user: UserDetail }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const set = useAction(() => apiPost(`/users/${user.uid}/password`, setPasswordResponseSchema, { password }));

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="set-password">
      <h3 className="font-semibold text-slate-900">{t('users.setPassword')}</h3>
      <p className="mt-1 text-sm text-slate-500">{t('users.setPasswordHint')}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setDone(false);
          }}
          minLength={8}
          data-testid="set-password-input"
          className="latin-inline rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900"
        />
        <button
          type="button"
          onClick={() =>
            void set.run().then((r) => {
              if (r) {
                setPassword('');
                setDone(true);
              }
            })
          }
          disabled={set.busy || password.length < 8}
          data-testid="set-password-submit"
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40"
        >
          {set.busy ? t('users.saving') : t('users.setPassword')}
        </button>
      </div>
      {done && (
        <p className="mt-2 text-sm text-emerald-700" data-testid="set-password-done">
          {t('users.passwordSet')}
        </p>
      )}
      <div className="mt-2">
        <ActionError error={set.error} />
      </div>
    </section>
  );
}

/** The shape the server attaches to a 409 when a soldier still holds gear. */
const blockersSchema = z.object({ openAssignments: z.array(openAssignmentSchema) });

/**
 * The item's name in the reader's language.
 *
 * Catalog items carry both nameHe and nameEn (§2.8). Hebrew is the fallback
 * when English is missing, never the other way round — Hebrew is what the unit
 * actually reads.
 */
function itemName(item: OpenAssignment, language: string): string | null {
  return language.startsWith('en') ? (item.nameEn ?? item.nameHe) : (item.nameHe ?? item.nameEn);
}

function StatusCard({ user, onChanged }: { user: UserDetail; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [blockers, setBlockers] = useState<OpenAssignment[]>([]);

  const setStatus = useCallback(
    async (status: UserStatus) => {
      setBusy(true);
      setError(null);
      setBlockers([]);
      try {
        await apiPost(`/users/${user.uid}/status`, userDetailResponseSchema, { status });
        onChanged();
      } catch (e) {
        if (!(e instanceof ApiClientError)) return;
        setError(e);
        // §2.7: a refusal names what is still out. Without the list, "blocked"
        // is an instruction to go and find the paperwork by hand.
        const parsed = blockersSchema.safeParse(e.details);
        if (parsed.success) setBlockers(parsed.data.openAssignments);
      } finally {
        setBusy(false);
      }
    },
    [user.uid, onChanged],
  );

  return (
    <section
      className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200"
      data-testid="status-editor"
    >
      <h3 className="font-semibold text-slate-900">{t('users.changeStatus')}</h3>
      <p className="mt-1 text-sm text-slate-500">{t('users.deactivateHint')}</p>

      <div className="mt-3 flex flex-wrap gap-2">
        {userStatusSchema.options.map((status) => (
          <button
            key={status}
            type="button"
            disabled={busy || user.status === status}
            onClick={() => void setStatus(status)}
            data-testid={`set-status-${status}`}
            className="rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100 disabled:opacity-40"
          >
            {t(`status.${status}`)}
          </button>
        ))}
      </div>

      {/* The generic `conflict` string is "this clashed with another change,
          try again" — true of a race, wrong here. A deactivation refused for
          open gear is a business rule with a specific explanation, and showing
          both would tell the reader to retry something that will never succeed
          until the gear comes back. */}
      {error && blockers.length === 0 && (
        <div className="mt-3">
          <ApiError code={error.code} requestId={error.requestId} />
        </div>
      )}

      {blockers.length > 0 && (
        <div
          role="alert"
          className="mt-3 rounded-xl bg-amber-50 p-3 ring-1 ring-amber-200"
          data-testid="deactivation-blocked"
        >
          <p className="text-sm font-medium text-amber-900">
            {t('users.stillHolding', { count: blockers.length })}
          </p>
          <ul className="mt-2 space-y-1" data-testid="open-assignments">
            {blockers.map((item) => (
              <li key={item.assignmentId} className="text-sm text-amber-800">
                {itemName(item, i18n.language) ?? (
                  <span className="latin-inline">{item.catalogItemId}</span>
                )}
                {item.inventoryUnitId && (
                  <>
                    {' · '}
                    {/* A serial is Latin script in an RTL run: isolate it. */}
                    <span className="latin-inline">{item.inventoryUnitId}</span>
                  </>
                )}
                {' · '}
                {t(`assignmentStatus.${item.status}`)}
              </li>
            ))}
          </ul>
          {error?.requestId && (
            <p className="mt-2 text-xs text-amber-700">
              <span className="latin-inline">{error.requestId}</span>
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/** Move the soldier to another unit — a team, or a platoon itself (story 32). The server derives the rest. */
function MoveTeamCard({ user, onChanged }: { user: UserDetail; onChanged: () => void }) {
  const { t } = useTranslation();
  const units = useApi('/units', unitsResponseSchema);
  const current = user.teamId ?? user.platoonId ?? '';
  const [unitId, setUnitId] = useState(current);
  const move = useAction((target: string) =>
    apiPatch(`/users/${user.uid}`, userDetailResponseSchema, { unitId: target }),
  );

  if (units.kind !== 'ready') return null;

  const options = unitOptions(units.data.units, (name) => t('users.directOnPlatoon', { platoon: name }));

  const save = async () => {
    if (unitId === current) return;
    if (await move.run(unitId)) onChanged();
  };

  return (
    <section
      className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200"
      data-testid="move-team"
    >
      <h3 className="font-semibold text-slate-900">{t('users.moveTeam')}</h3>
      <p className="mt-1 text-sm text-slate-500">{t('users.moveTeamHint')}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select
          value={unitId}
          onChange={(e) => setUnitId(e.target.value)}
          data-testid="move-team-select"
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900"
        >
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void save()}
          disabled={move.busy || unitId === current}
          data-testid="move-team-save"
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40"
        >
          {t('users.move')}
        </button>
      </div>
      <div className="mt-3">
        <ActionError error={move.error} />
      </div>
    </section>
  );
}
